import { useState, useEffect, useMemo, useCallback } from 'react';
import { listSheets, deleteSheet, bulkDeleteSheets, deserializeSheet, getSheet } from '@/lib/store-sheet';
import { JEWELRY_TYPES, calcTotalStoneWeight } from '@/utils/cad-sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Pencil, Eye, Search, X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LivePreview } from './LivePreview';
import { toPng } from 'html-to-image';

export function AllSheetsPanel({ onEditSheet, refreshKey }) {
  const [sheets, setSheets] = useState([]);
  const [styleSearch, setStyleSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [jewelryFilter, setJewelryFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [previewData, setPreviewData] = useState(null);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompanyFooter, setShowCompanyFooter] = useState(true);

  const loadSheets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSheets();
      setSheets(data);
    } catch { setSheets([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadSheets();
    setSelected(new Set());
  }, [refreshKey, loadSheets]);

  const filtered = useMemo(() => {
    let result = sheets;
    if (jewelryFilter !== 'all') result = result.filter(s => s.jewelryType === jewelryFilter);
    if (styleSearch.trim()) { const q = styleSearch.toLowerCase(); result = result.filter(s => s.styleNo.toLowerCase().includes(q)); }
    if (customerSearch.trim()) { const q = customerSearch.toLowerCase(); result = result.filter(s => (s.customerName || '').toLowerCase().includes(q)); }
    return result;
  }, [sheets, styleSearch, customerSearch, jewelryFilter]);

  const hasActiveFilters = jewelryFilter !== 'all' || styleSearch.trim() || customerSearch.trim();

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(s => s.id)));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} sheet(s) permanently?`)) return;
    const count = selected.size;
    await bulkDeleteSheets([...selected]);
    await loadSheets();
    setSelected(new Set());
    toast.success(`${count} sheet(s) deleted`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sheet permanently?')) return;
    await deleteSheet(id);
    await loadSheets();
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    toast.success('Sheet deleted');
  };

  const handlePreview = async (id) => {
    const stored = await getSheet(id);
    if (stored) setPreviewData(deserializeSheet(stored));
  };

  const handleEdit = async (id) => {
    const stored = await getSheet(id);
    if (stored) { onEditSheet(deserializeSheet(stored), id); toast.success('Sheet loaded for editing'); }
  };

  const handleBulkDownload = async () => {
    if (selected.size === 0) return;
    setBulkExporting(true);
    toast.info(`Exporting ${selected.size} sheet(s)...`);
    for (const id of selected) {
      const stored = await getSheet(id);
      if (!stored) continue;
      const sheetData = deserializeSheet(stored);
      const container = document.createElement('div');
      container.style.position = 'fixed'; container.style.left = '-9999px'; container.style.top = '0';
      document.body.appendChild(container);
      const el = document.createElement('div');
      el.style.width = '800px'; el.style.backgroundColor = '#ffffff'; el.style.fontFamily = 'Arial, Helvetica, sans-serif'; el.style.color = '#000000'; el.style.border = '2px solid #000';
      const totalSW = calcTotalStoneWeight(sheetData.stones);
      const totalSQ = sheetData.stones.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
      while (el.firstChild) el.removeChild(el.firstChild);
      // Build header safely using textContent
      const header = document.createElement('div');
      header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:14px 20px;border-bottom:2px solid #000';
      const leftDiv = document.createElement('div');
      const styleDiv = document.createElement('div');
      styleDiv.style.cssText = 'font-size:20px;font-weight:bold';
      styleDiv.textContent = `Style No: ${sheetData.styleNo || '\u2014'}`;
      leftDiv.appendChild(styleDiv);
      const metalInfo = document.createElement('div');
      metalInfo.style.cssText = 'font-size:15px;color:#444;margin-top:1px';
      metalInfo.textContent = `${sheetData.metals.map(m => `${m.karat} ${m.color}${m.netWeight ? ` (${m.netWeight}g)` : ''}`).join(' / ')}${sheetData.netWeight ? ` | Net: ${sheetData.netWeight}g` : ''}${sheetData.grossWeight ? ` | Gross: ${sheetData.grossWeight}g` : ''}${sheetData.size ? ` | Size: ${sheetData.size}` : ''}`;
      leftDiv.appendChild(metalInfo);
      const typeDiv = document.createElement('div');
      typeDiv.style.cssText = 'font-size:15px;color:#666';
      typeDiv.textContent = sheetData.jewelryType;
      leftDiv.appendChild(typeDiv);
      header.appendChild(leftDiv);
      const rightDiv = document.createElement('div');
      rightDiv.style.textAlign = 'right';
      const dateDiv = document.createElement('div');
      dateDiv.style.cssText = 'font-size:15px;color:#444';
      dateDiv.textContent = `Date: ${sheetData.date}`;
      rightDiv.appendChild(dateDiv);
      if (sheetData.designerName) {
        const designerDiv = document.createElement('div');
        designerDiv.style.cssText = 'font-size:15px;font-weight:700';
        designerDiv.textContent = `#${sheetData.designerName}`;
        rightDiv.appendChild(designerDiv);
      }
      header.appendChild(rightDiv);
      el.appendChild(header);
      if (showCompanyFooter) {
        const footer = document.createElement('div');
        footer.style.cssText = 'border-top:1px solid #ccc;padding:5px 16px;text-align:center;font-size:11px;color:#999';
        footer.textContent = "K's Jewelry \u2014 CAD Sheet";
        el.appendChild(footer);
      }
      container.appendChild(el);
      try {
        await new Promise(r => setTimeout(r, 200));
        const dataUrl = await toPng(el, { pixelRatio: 3, backgroundColor: '#ffffff' });
        const link = document.createElement('a');
        link.download = `${sheetData.styleNo || 'CAD-Sheet'}.png`;
        link.href = dataUrl; link.click();
      } catch (err) { console.error('Bulk export failed for', id, err); }
      finally { document.body.removeChild(container); }
    }
    setBulkExporting(false);
    toast.success('Bulk export complete');
  };

  return (
    <div className="flex flex-col h-full" data-testid="cad-all-sheets">
      <div className="p-4 border-b border-border space-y-2 bg-card/50">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={styleSearch} onChange={e => setStyleSearch(e.target.value)} placeholder="Search by Style No..." className="h-8 text-xs pl-8" />
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search by Customer..." className="h-8 text-xs pl-8" />
          </div>
          <Select value={jewelryFilter} onValueChange={setJewelryFilter}>
            <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {JEWELRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setStyleSearch(''); setCustomerSearch(''); setJewelryFilter('all'); }} title="Clear filters"><X className="h-3.5 w-3.5" /></Button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleSelectAll} className="h-3.5 w-3.5" />
            <span className="text-xs text-muted-foreground">{selected.size > 0 ? `${selected.size} selected` : `${filtered.length} sheets`}</span>
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleBulkDownload} disabled={bulkExporting}>
                {bulkExporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}Download PNGs ({selected.size})
              </Button>
              <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={handleBulkDelete}>
                <Trash2 className="h-3 w-3 mr-1" /> Delete ({selected.size})
              </Button>
            </div>
          )}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer ml-auto" data-testid="footer-toggle">
            <Checkbox checked={showCompanyFooter} onCheckedChange={setShowCompanyFooter} className="h-3.5 w-3.5" />
            Show company footer
          </label>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-center text-muted-foreground py-16"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /><p className="text-sm">Loading sheets...</p></div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-16"><p className="text-sm">{hasActiveFilters ? 'No matching sheets.' : 'No saved sheets yet.'}</p></div>
        ) : (
          <div className="space-y-2">
            {filtered.map(sheet => (
              <div key={sheet.id} className={`border rounded-lg p-3 transition-colors ${selected.has(sheet.id) ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/30'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox checked={selected.has(sheet.id)} onCheckedChange={() => toggleSelect(sheet.id)} className="mt-0.5 h-4 w-4" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-foreground truncate">{sheet.styleNo || 'Untitled'}</div>
                    {sheet.customerName && (<div className="text-xs text-muted-foreground truncate">{sheet.customerName}{sheet.customerDesignNo && ` \u2022 ${sheet.customerDesignNo}`}</div>)}
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="px-1.5 py-0.5 rounded bg-muted text-[11px]">{sheet.jewelryType}</span>
                      <span>{sheet.metals.map(m => m.karat).join('/')}</span>
                      <span>{'\u2022'}</span>
                      <span>{new Date(sheet.savedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => handlePreview(sheet.id)} title="Preview"><Eye className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => handleEdit(sheet.id)} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(sheet.id)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {previewData && <LivePreview data={previewData} open={true} onClose={() => setPreviewData(null)} />}
    </div>
  );
}
